/**
 * GTM 调试工具
 * 用于实时监控和统计 dataLayer 事件
 */

interface GTMEvent {
  event: string;
  timestamp: number;
  data: any;
}

class GTMDebugger {
  private events: GTMEvent[] = [];
  private isEnabled: boolean = false;

  /**
   * 启用 GTM 调试
   */
  enable() {
    if (this.isEnabled) return;
    this.isEnabled = true;

    // 拦截 dataLayer.push
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      const originalPush = (window as any).dataLayer.push;
      const self = this;

      (window as any).dataLayer.push = function(...args: any[]) {
        // 记录事件
        args.forEach((item: any) => {
          if (item.event) {
            self.events.push({
              event: item.event,
              timestamp: Date.now(),
              data: item,
            });
            console.log(`📊 GTM Event: ${item.event}`, item);
          }
        });

        // 调用原始方法
        return originalPush.apply(this, args);
      };

      console.log('✅ GTM Debugger enabled');
    }
  }

  /**
   * 禁用 GTM 调试
   */
  disable() {
    this.isEnabled = false;
    console.log('❌ GTM Debugger disabled');
  }

  /**
   * 获取所有事件
   */
  getEvents(): GTMEvent[] {
    return this.events;
  }

  /**
   * 获取事件统计
   */
  getEventStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    
    this.events.forEach(event => {
      if (!stats[event.event]) {
        stats[event.event] = 0;
      }
      stats[event.event]++;
    });

    return stats;
  }

  /**
   * 打印事件统计
   */
  printStats() {
    const stats = this.getEventStats();
    
    console.log('📊 GTM Event Statistics:');
    console.table(stats);
    
    return stats;
  }

  /**
   * 获取特定事件
   */
  getEventsByName(eventName: string): GTMEvent[] {
    return this.events.filter(e => e.event === eventName);
  }

  /**
   * 清空事件记录
   */
  clear() {
    this.events = [];
    console.log('🗑️ GTM events cleared');
  }

  /**
   * 导出事件数据
   */
  export(): string {
    return JSON.stringify(this.events, null, 2);
  }

  /**
   * 验证表单事件逻辑
   */
  validateFormEvents(): {
    valid: boolean;
    message: string;
    stats: {
      form_submit: number;
      form_submit_success: number;
      form_error: number;
    };
  } {
    const stats = this.getEventStats();
    
    const form_submit = stats['form_submit'] || 0;
    const form_submit_success = stats['form_submit_success'] || 0;
    const form_error = stats['form_error'] || 0;

    const valid = form_submit >= (form_submit_success + form_error);

    return {
      valid,
      message: valid 
        ? `✅ 数据一致: form_submit (${form_submit}) >= form_submit_success (${form_submit_success}) + form_error (${form_error})`
        : `❌ 数据异常: form_submit (${form_submit}) < form_submit_success (${form_submit_success}) + form_error (${form_error})`,
      stats: {
        form_submit,
        form_submit_success,
        form_error,
      },
    };
  }
}

// 创建全局实例
const gtmDebugger = new GTMDebugger();

// 暴露到 window 对象，方便在控制台使用
if (typeof window !== 'undefined') {
  (window as any).gtmDebugger = gtmDebugger;
}

export default gtmDebugger;

/**
 * 使用方法：
 * 
 * 1. 在浏览器控制台启用调试：
 *    gtmDebugger.enable()
 * 
 * 2. 提交表单
 * 
 * 3. 查看统计：
 *    gtmDebugger.printStats()
 * 
 * 4. 验证表单事件：
 *    gtmDebugger.validateFormEvents()
 * 
 * 5. 查看特定事件：
 *    gtmDebugger.getEventsByName('form_submit')
 * 
 * 6. 导出数据：
 *    gtmDebugger.export()
 * 
 * 7. 清空记录：
 *    gtmDebugger.clear()
 */

